import type { KeyboardEvent } from 'react';
import type { AutocompleteData } from '@/utils/autocompleteHelper';
import type { BoundingBoxType } from '@/fetchers/getQueryParserResult';
import _ from 'lodash';
import { queryStringConstants } from '@/constants/queryConstants';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { DEFAULT_ZOOM } from '@/constants/mapConstants';

type MapOptions = google.maps.MapOptions;
type GeoCoder = google.maps.Geocoder;
type LatLng = google.maps.LatLngLiteral;
type LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral;
type MapMouseEvent = google.maps.MapMouseEvent;
type GoogleMapType = google.maps.Map;
type GeocoderResult = google.maps.GeocoderResult;
const mapOptions: MapOptions = {
  minZoom: 1,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
  zoomControlOptions: {
    // google.maps.ControlPosition.LEFT_TOP. https://developers.google.com/maps/documentation/javascript/controls#ControlPositioning
    position: 5
  },
  styles: [
    {
      featureType: 'poi',
      stylers: [
        {
          visibility: 'off'
        }
      ]
    }
  ],
  gestureHandling: 'greedy'
};

const GOOGLE_TILE_SIZE = 256;

const DEFAULT_BOUNDS = {
  northEastPoint: {
    lat: 0,
    lon: 0
  },
  southWestPoint: {
    lat: 0,
    lon: 0
  }
};

type MapLocale = {
  language: string;
  region: string;
};

const Z_INDEX_MAP_VENUE_FLYOUT: number = 10;
const Z_INDEX_MAP_VENUE_ACTIVE_PIN: number = 1;
const Z_INDEX_MAP_VENUE_DEFAULT: number = 0;

// To provide custom ranks to the address types in map results
const ADDRESS_TYPE_PRE_RANK = {
  street_address: 1,
  postal_code: 2,
  premise: 3,
  route: 4,
  plus_code: 5,
  administrative_area_level_2: 6,
  administrative_area_level_1: 7,
  political: 8,
  country: 9
};
async function reverseGeocode(geocoder: GeoCoder, latlng: LatLng): Promise<AutocompleteData> {
  const response = await geocoder.geocode({ location: latlng });
  const sortedResults = sortGeocodeResult(response.results);
  const result = sortedResults[0];
  return { queryString: result.formatted_address, id: result.place_id, types: result.types, provider: 'google' };
}

function sortGeocodeResult(results: GeocoderResult[]) {
  return _.sortBy(results, result => {
    return ADDRESS_TYPE_PRE_RANK[result.types[0]] || 10;
  });
}
function getMapLocale(locale: string): MapLocale {
  const [language, region] = locale.split('-');
  return {
    language,
    region
  };
}
function isValidLatLng(latlng: LatLng): boolean {
  return latlng.lat >= -90 && latlng.lat <= 90 && latlng.lng >= -180 && latlng.lng <= 180;
}

function isFullMapView(params: ReadonlyURLSearchParams): boolean {
  return params.has(queryStringConstants.VIEW) && params.get(queryStringConstants.VIEW) === 'FULL_MAP';
}

/**
 * Keyboard event to avoid creating new keypress for each click event that we attach to non-interactive elements, eg. div
 * callback -> onclick event handler
 */
function onKeyPress(e: KeyboardEvent, callback: () => void, ...triggerKeys: string[]): void {
  e.stopPropagation();
  if (triggerKeys.length === 0 && (e.key === 'Enter' || e.key === ' ')) {
    callback();
  } else if (triggerKeys.indexOf(e.key) !== -1) {
    callback();
  }
}

function isCoordinatesEqual(pos1: LatLng, pos2: LatLng): boolean {
  const cord1 = new google.maps.LatLng(pos1);
  const cord2 = new google.maps.LatLng(pos2);
  return cord1.equals(cord2);
}

/**
 * returns z index for painting map markers so that active venues always comes on top
 */
function getZIndexOfPin(venueId: string, venueFlyoutId: string, hoveredVenueId: string | null): number {
  if (venueFlyoutId === venueId) {
    return Z_INDEX_MAP_VENUE_FLYOUT;
  }
  if (venueId === hoveredVenueId) {
    return Z_INDEX_MAP_VENUE_ACTIVE_PIN;
  }
  return Z_INDEX_MAP_VENUE_DEFAULT;
}

/*
 * Allowing Google Map React's latLng2World and world2LatLng functions to be used
 * in MapViewContainer
 */
function latLng2World(_ref: LatLng): { x: number; y: number } {
  const { lat } = _ref;
  const { lng } = _ref;

  const sin = Math.sin((lat * Math.PI) / 180);
  const x = lng / 360 + 0.5;
  let y = 0.5 - (0.25 * Math.log((1 + sin) / (1 - sin))) / Math.PI;

  if (y < 0) {
    y = 0;
  } else if (y > 1) {
    y = 1;
  }
  return { x, y };
}

function world2LatLng(_ref2: { x: number; y: number }): LatLng {
  const { x } = _ref2;
  const { y } = _ref2;

  const n = Math.PI - 2 * Math.PI * y;

  return {
    lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))),
    lng: x * 360 - 180
  };
}

// return new bounds for a given zoom level
function scaleBoundsToZoom(center: LatLng, zoom: number, size: { width: number; height: number }): BoundingBoxType {
  const middle = latLng2World(center);

  const scale = 2 ** zoom;
  const { width } = size;
  const { height } = size;
  const halfW = width / scale / GOOGLE_TILE_SIZE / 2;
  const halfH = height / scale / GOOGLE_TILE_SIZE / 2;

  const newNW = world2LatLng({
    x: middle.x - halfW,
    y: middle.y - halfH
  });

  const newSE = world2LatLng({
    x: middle.x + halfW,
    y: middle.y + halfH
  });

  return {
    northEastPoint: {
      lat: newNW.lat,
      lon: newSE.lng
    },
    southWestPoint: {
      lat: newSE.lat,
      lon: newNW.lng
    }
  };
}

function fitNwSe(
  mapBounds: {
    nw: LatLng;
    se: LatLng;
  },
  size: { width: number; height: number }
) {
  const EPS = 0.000000001;
  const nwWorld = latLng2World(mapBounds.nw);
  const seWorld = latLng2World(mapBounds.se);
  const dx = nwWorld.x < seWorld.x ? seWorld.x - nwWorld.x : 1 - nwWorld.x + seWorld.x;
  const dy = seWorld.y - nwWorld.y;

  if (dx <= 0 && dy <= 0) {
    return null;
  }

  const zoomX = Math.log2(size.width / GOOGLE_TILE_SIZE / Math.abs(dx));
  const zoomY = Math.log2(size.height / GOOGLE_TILE_SIZE / Math.abs(dy));
  const zoom = Math.floor(EPS + Math.min(zoomX, zoomY));

  const middle = {
    x:
      // eslint-disable-next-line no-nested-ternary
      nwWorld.x < seWorld.x
        ? 0.5 * (nwWorld.x + seWorld.x)
        : nwWorld.x + seWorld.x - 1 > 0
        ? 0.5 * (nwWorld.x + seWorld.x - 1)
        : 0.5 * (1 + nwWorld.x + seWorld.x),
    y: 0.5 * (nwWorld.y + seWorld.y)
  };

  const scale = 2 ** zoom;
  const halfW = size.width / scale / GOOGLE_TILE_SIZE / 2;
  const halfH = size.height / scale / GOOGLE_TILE_SIZE / 2;

  const newNW = world2LatLng({
    x: middle.x - halfW,
    y: middle.y - halfH
  });

  const newSE = world2LatLng({
    x: middle.x + halfW,
    y: middle.y + halfH
  });

  return {
    center: world2LatLng(middle),
    zoom,
    newBounds: {
      northEastPoint: {
        lat: newNW.lat,
        lon: newSE.lng
      },
      southWestPoint: {
        lat: newSE.lat,
        lon: newNW.lng
      }
    }
  };
}

/**
 * Find the zoom of the passed map size, center abd bounds.
 * Code reference taken from https://github.com/google-map-react/google-map-react/blob/master/src/lib/index.js#L150
 */
function fitBounds(
  size: { width: number; height: number },
  center: LatLng,
  bounds?: BoundingBoxType
): {
  center: LatLng;
  zoom: number;
  newBounds: BoundingBoxType;
} {
  if (!bounds) {
    return (
      fitNwSe(
        {
          nw: {
            lat: DEFAULT_BOUNDS.northEastPoint.lat,
            lng: DEFAULT_BOUNDS.southWestPoint.lon
          },
          se: {
            lat: DEFAULT_BOUNDS.southWestPoint.lat,
            lng: DEFAULT_BOUNDS.northEastPoint.lon
          }
        },
        size
      ) || {
        center,
        zoom: DEFAULT_ZOOM,
        newBounds: DEFAULT_BOUNDS
      }
    );
  }
  return (
    fitNwSe(
      {
        nw: {
          lat: bounds.northEastPoint.lat,
          lng: bounds.southWestPoint.lon
        },
        se: {
          lat: bounds.southWestPoint.lat,
          lng: bounds.northEastPoint.lon
        }
      },
      size
    ) || {
      center,
      zoom: DEFAULT_ZOOM,
      newBounds: DEFAULT_BOUNDS
    }
  );
}

export {
  mapOptions,
  getMapLocale,
  reverseGeocode,
  isValidLatLng,
  latLng2World,
  world2LatLng,
  scaleBoundsToZoom,
  fitBounds,
  onKeyPress,
  isCoordinatesEqual,
  getZIndexOfPin,
  isFullMapView
};

export type { LatLng, GeoCoder, MapMouseEvent, GoogleMapType, LatLngBoundsLiteral };
